'use client';

import { StatusBadge } from '@/components/common';
import { ItemTrackingStatus, type RoadmapStatusItem, type RoadmapStatusWithResourceDTO } from '@/domain/roadmap/types';
import { getStatusColor } from '@/features/roadmap/components/RoadMapEditDialog/PlanningTab/utils';
import { useRoadmapEditStore } from '@/features/roadmap/store/roadmap';
import { useAuth } from '@/hooks/useAuth';
import { canEditRoadmap } from '@/utils/roadmapPermission';
import { Typography } from '@andritzot/metris-web-ui/data-display/typography';
import { FormControl } from '@andritzot/metris-web-ui/inputs/form-control';
import { IconButton } from '@andritzot/metris-web-ui/inputs/icon-button';
import { Select } from '@andritzot/metris-web-ui/inputs/select';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Stack } from '@andritzot/metris-web-ui/layout/stack';
import { DataGrid, type GridColDef } from '@andritzot/metris-web-ui/mui-x/data-grid';
import { MenuItem } from '@andritzot/metris-web-ui/navigation/menu/menu-item';
import ChatBubbleOutlineOutlined from '@mui/icons-material/ChatBubbleOutlineOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ItemCommentPopover } from './ItemCommentPopover';
import { ItemHistoryPopover, type PendingChange } from './ItemHistoryPopover';

const ITEM_TRACKING_STATUS_VALUES: ReadonlySet<string> = new Set<string>(Object.values(ItemTrackingStatus));

function isItemTrackingStatus(value: unknown): value is ItemTrackingStatus {
  return typeof value === 'string' && ITEM_TRACKING_STATUS_VALUES.has(value);
}

interface PlanningDataTableProps {
  statuses: RoadmapStatusWithResourceDTO[];
  selectedItemIds?: Set<number>;
  onSelectionChange?: (ids: Set<number>) => void;
  onItemEdit: (itemId: number, field: 'status' | 'qualityAssurance', value: ItemTrackingStatus | boolean) => void;
  viewOnly?: boolean;
  isLoading?: boolean;
}

const STATUS_OPTIONS: ItemTrackingStatus[] = [
  ItemTrackingStatus.NotStarted,
  ItemTrackingStatus.InProgress,
  ItemTrackingStatus.Completed,
  ItemTrackingStatus.Delayed,
  ItemTrackingStatus.NotApplicable,
];

function formatFieldValue(field: 'status' | 'qualityAssurance', value: ItemTrackingStatus | boolean): string {
  if (typeof value === 'boolean') {
    return value ? 'Approved' : 'Pending';
  }
  return String(value);
}

interface FlatRow {
  id: number;
  phaseName: string;
  isPhaseStart: boolean;
  item: RoadmapStatusItem;
}

interface TableItemActionsProps {
  itemId: number;
  pendingChange?: PendingChange;
}

function TableItemActions({ itemId, pendingChange }: Readonly<TableItemActionsProps>) {
  const [historyAnchor, setHistoryAnchor] = useState<HTMLElement | null>(null);
  const [commentAnchor, setCommentAnchor] = useState<HTMLElement | null>(null);
  const roadmapId = useRoadmapEditStore((s) => s.editableRoadMap.idRoadmap);

  return (
    <Stack direction="row" sx={{ flexShrink: 0 }}>
      <IconButton
        size="small"
        sx={{ p: 0.25 }}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          setHistoryAnchor(e.currentTarget);
        }}
        aria-label="Show last modifier"
      >
        <InfoOutlined sx={{ fontSize: 16 }} />
      </IconButton>
      <IconButton
        size="small"
        sx={{ p: 0.25 }}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          setCommentAnchor(e.currentTarget);
        }}
        aria-label="Show comments"
      >
        <ChatBubbleOutlineOutlined sx={{ fontSize: 16 }} />
      </IconButton>
      {historyAnchor && (
        <ItemHistoryPopover
          roadmapId={roadmapId}
          itemId={itemId}
          anchorEl={historyAnchor}
          onClose={() => setHistoryAnchor(null)}
          pendingChange={pendingChange}
        />
      )}
      {commentAnchor && (
        <ItemCommentPopover itemId={itemId} anchorEl={commentAnchor} onClose={() => setCommentAnchor(null)} />
      )}
    </Stack>
  );
}

type ItemEditFn = (itemId: number, field: 'status' | 'qualityAssurance', value: ItemTrackingStatus | boolean) => void;

interface StatusCellProps {
  item: RoadmapStatusItem;
  viewOnly?: boolean;
  onEditRef: React.RefObject<ItemEditFn>;
}

const StatusCell = React.memo(function StatusCell({ item, viewOnly, onEditRef }: Readonly<StatusCellProps>) {
  return (
    <FormControl size="small" variant="standard" sx={{ width: '100%' }}>
      <Select
        disableUnderline
        disabled={viewOnly}
        value={item.status}
        onChange={(e) => {
          const val = e.target.value;
          if (isItemTrackingStatus(val)) {
            onEditRef.current(item.idRoadmapStatusItem, 'status', val);
          }
        }}
        renderValue={(val) => {
          const status = isItemTrackingStatus(val) ? val : ItemTrackingStatus.NotStarted;
          return <StatusBadge color={getStatusColor(status)}>{String(val)}</StatusBadge>;
        }}
        sx={{ fontSize: 13 }}
      >
        {STATUS_OPTIONS.map((opt) => (
          <MenuItem key={opt} value={opt}>
            <StatusBadge color={getStatusColor(opt)}>{opt}</StatusBadge>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
});

interface QaCellProps {
  item: RoadmapStatusItem;
  viewOnly?: boolean;
  onEditRef: React.RefObject<ItemEditFn>;
}

const QaCell = React.memo(function QaCell({ item, viewOnly, onEditRef }: Readonly<QaCellProps>) {
  return (
    <FormControl size="small" variant="standard" sx={{ width: '100%' }}>
      <Select
        disableUnderline
        disabled={viewOnly}
        value={item.qualityAssurance ? 'true' : 'false'}
        onChange={(e) => onEditRef.current(item.idRoadmapStatusItem, 'qualityAssurance', e.target.value === 'true')}
        renderValue={(val) =>
          val === 'true' ? (
            <StatusBadge color="green">Approved</StatusBadge>
          ) : (
            <StatusBadge color="grey">Pending</StatusBadge>
          )
        }
        sx={{ fontSize: 13 }}
      >
        <MenuItem value="true">
          <StatusBadge color="green">Approved</StatusBadge>
        </MenuItem>
        <MenuItem value="false">
          <StatusBadge color="grey">Pending</StatusBadge>
        </MenuItem>
      </Select>
    </FormControl>
  );
});

export function PlanningDataTable({
  statuses,
  selectedItemIds,
  onSelectionChange,
  onItemEdit,
  viewOnly,
  isLoading,
}: Readonly<PlanningDataTableProps>) {
  const [pendingChanges, setPendingChanges] = useState<Map<number, PendingChange>>(new Map());
  const { user } = useAuth();
  const editableRoadMap = useRoadmapEditStore((s) => s.editableRoadMap);

  const isSelectable =
    !!onSelectionChange &&
    canEditRoadmap(user, {
      ...editableRoadMap,
      product: editableRoadMap.product ?? null,
    });

  const rows = useMemo<FlatRow[]>(
    () =>
      statuses.flatMap((phase, phaseIndex) =>
        phase.roadmapStatusItems.map((item, itemIndex) => ({
          id: item.idRoadmapStatusItem,
          phaseName: phase.name,
          isPhaseStart: itemIndex === 0 && phaseIndex > 0,
          item,
        })),
      ),
    [statuses],
  );

  const handleItemEdit = useCallback(
    (itemId: number, field: 'status' | 'qualityAssurance', value: ItemTrackingStatus | boolean) => {
      const currentItem = rows.find((r) => r.item.idRoadmapStatusItem === itemId)?.item;
      const rawOldValue = field === 'qualityAssurance' ? currentItem?.qualityAssurance : currentItem?.status;
      if (rawOldValue === value) return;
      const oldValue = rawOldValue === undefined ? '' : formatFieldValue(field, rawOldValue);
      const newValue = formatFieldValue(field, value);
      setPendingChanges((prev) =>
        new Map(prev).set(itemId, { field, oldValue, newValue, changedAt: new Date(), userName: user?.name ?? 'You' }),
      );
      onItemEdit(itemId, field, value);
    },
    [onItemEdit, rows, user],
  );

  const handleItemEditRef = useRef<ItemEditFn>(handleItemEdit);
  useEffect(() => {
    handleItemEditRef.current = handleItemEdit;
  }, [handleItemEdit]);

  const columns = useMemo<GridColDef<FlatRow>[]>(
    () => [
      { field: 'phaseName', headerName: 'Phase', minWidth: 140, flex: 1 },
      {
        field: 'name',
        headerName: 'Steps',
        minWidth: 200,
        flex: 2,
        valueGetter: (_value, row) => row.item.name,
      },
      {
        field: 'teamMembers',
        headerName: 'Team Members',
        minWidth: 180,
        flex: 2,
        valueGetter: (_value, row) => row.item.teamMembers,
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 160,
        sortable: false,
        renderCell: ({ row }) => <StatusCell item={row.item} viewOnly={viewOnly} onEditRef={handleItemEditRef} />,
      },
      {
        field: 'qualityAssurance',
        headerName: 'QA',
        minWidth: 130,
        sortable: false,
        renderCell: ({ row }) => <QaCell item={row.item} viewOnly={viewOnly} onEditRef={handleItemEditRef} />,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 72,
        sortable: false,
        renderCell: ({ row }) => (
          <TableItemActions
            itemId={row.item.idRoadmapStatusItem}
            pendingChange={pendingChanges.get(row.item.idRoadmapStatusItem)}
          />
        ),
      },
    ],
    [viewOnly, pendingChanges],
  );

  const rowSelectionModel = useMemo(
    () => ({ type: 'include' as const, ids: new Set(selectedItemIds ?? []) }),
    [selectedItemIds],
  );

  if (rows.length === 0 && !isLoading) {
    return (
      <Stack
        direction="row"
        spacing={1}
        sx={{ justifyContent: 'center', alignItems: 'center', py: 6, color: 'text.secondary' }}
      >
        <InfoOutlined fontSize="small" />
        <Typography variant="body2">No planning data available.</Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <DataGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        density="compact"
        disableColumnMenu
        pagination={false}
        autoHeight
        disableRowSelectionOnClick
        checkboxSelection={isSelectable}
        rowSelectionModel={rowSelectionModel}
        slotProps={{
          loadingOverlay: {
            variant: 'circular-progress',
            noRowsVariant: 'circular-progress',
          },
        }}
        onRowSelectionModelChange={(model) => {
          if (model.type === 'include') {
            onSelectionChange?.(new Set([...model.ids].map(Number)));
          } else {
            const excludedIds = new Set([...model.ids].map(Number));
            onSelectionChange?.(new Set(rows.map((r) => r.id).filter((id) => !excludedIds.has(id))));
          }
        }}
        getRowClassName={({ row }) => (row.isPhaseStart ? 'phase-divider' : '')}
        sx={{
          '& .MuiDataGrid-columnHeader': { bgcolor: 'primary.50', fontWeight: 600 },
          '& .MuiDataGrid-row.phase-divider .MuiDataGrid-cell': {
            borderTop: '2px solid',
            borderTopColor: 'primary.light',
          },
          '& .MuiDataGrid-cell[data-field="status"], & .MuiDataGrid-cell[data-field="qualityAssurance"]': {
            overflow: 'visible',
          },
        }}
      />
    </Box>
  );
}
